import {keepPreviousData, useInfiniteQuery, useQuery} from "@tanstack/react-query";
import {ApiConfig} from "@/config/api.config";
import {ApiHookProps} from '@/@types/api.types';
import {UrlHelper} from '@/utils/UrlHelper';
import {
    AdminNotificationsListResponseDto,
    OwnerNotificationsListItemResponseDto,
    OwnerNotificationsListResponseDto
} from '@/features/notifications/types/notifications-dto';
import {useNotificationsFilter} from '@/features/notifications/hooks/useNotificationsFilter';
import {Roles} from "@/@types/auth.types.ts";
import { format } from 'date-fns';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    searchText?: string;
    role?: Roles;
    isRead?: boolean;
    isRequired?: boolean;
} & ApiHookProps;

export const useNotificationsListApi = ({searchText, role, isRead, isRequired}: Props) => {
    const axiosInstance = useAxiosAuth();
    const {filter} = useNotificationsFilter();
    const {take, skip, type, startDate, endDate, category, orderBy, orderDirection} = filter;

    const {
        data: adminNotificationsListData,
        isLoading: isLoadingAdminNotificationsListData,
        isError: isErrorAdminNotificationsListData
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.notifications.queryKeys.adminList,
            searchText, take, skip, type, startDate, endDate, category, orderBy, orderDirection
        ],
        queryFn: async (): Promise<AdminNotificationsListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.notifications.endpoints.adminList, {
                searchText, take, skip, type, startDate, endDate, category, orderBy, orderDirection
            });
            const response = await axiosInstance.get<AdminNotificationsListResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: role && role === Roles.admin
    });

    const {
        data: ownerNotificationsListData,
        isLoading: isLoadingOwnerNotificationsListData,
        isError: isErrorOwnerNotificationsListData,
        fetchNextPage: fetchOwnerNotificationsListDataNextPage,
        hasNextPage: hasOwnerNotificationsListDataNextPage,
        isFetchingNextPage: isFetchingOwnerNotificationsListDataNextPage
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.notifications.queryKeys.ownerList,
            searchText, take, skip, type, startDate, endDate, category, orderBy, orderDirection, isRead, isRequired
        ],
        queryFn: async ({pageParam}): Promise<OwnerNotificationsListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.notifications.endpoints.ownerList, {
                searchText, take, skip: pageParam, type, startDate, endDate, category, orderBy, orderDirection, isRead, isRequired
            });
            const response = await axiosInstance.get<OwnerNotificationsListResponseDto>(url);            

            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
        enabled: role && role === Roles.owner
    });

    const fetchedData = ownerNotificationsListData?.pages
        .flatMap((page) => page.items ?? [])
        .reduce((acc, curr) => {
            const dateKey = format(curr.sent, 'yyyy-MM-dd');
            if (!acc[dateKey]) acc[dateKey] = [];            
            acc[dateKey].push(curr);
            return acc;
        }, {} as Record<string,  OwnerNotificationsListItemResponseDto[]>);

    return {
        adminNotificationsListData,
        isErrorAdminNotificationsListData,
        isLoadingAdminNotificationsListData,
        ownerNotificationsListData: fetchedData,
        isLoadingOwnerNotificationsListData,
        isErrorOwnerNotificationsListData,
        fetchOwnerNotificationsListDataNextPage,
        hasOwnerNotificationsListDataNextPage,
        isFetchingOwnerNotificationsListDataNextPage
    }
}